import { useCallback, useEffect, useState } from "react";
import { api, ApiError, ItemAuditEntry } from "../api";
import { formatTime } from "../utils";
import { GlassPanel, Select, SkeletonTable, ErrorState } from "../components";

/** yyyy-mm-dd → ISO đầu ngày (local). null nếu rỗng/sai. */
function dateOnlyStartToIso(v: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v.trim());
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 0, 0, 0, 0);
  return isNaN(d.getTime()) ? null : d.toISOString();
}
/** yyyy-mm-dd → ISO cuối ngày (local 23:59:59.999). */
function dateOnlyEndToIso(v: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v.trim());
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 23, 59, 59, 999);
  return isNaN(d.getTime()) ? null : d.toISOString();
}

const ACTION_LABELS: Record<string, string> = {
  create: "Tạo mới",
  update: "Cập nhật",
  delete: "Xoá",
  adjust_stock: "Điều chỉnh tồn",
  bulk_update: "Sửa hàng loạt",
  consume_material: "Tiêu hao nguyên liệu",
  push_loyverse: "Đẩy Loyverse",
};

function actionLabel(a: string): string {
  return ACTION_LABELS[a] || a;
}

const PAGE_LIMIT = 200;

/**
 * Lịch sử thay đổi hàng hoá (items_audit_log) — dành cho chủ shop.
 * Đọc từ GET /api/items/audit-log (worker tổng hợp từ items_audit_log; KHÔNG có store song song).
 * Lọc: theo hàng (q), khoảng ngày, loại thao tác.
 */
export default function ItemAuditHistory() {
  const [entries, setEntries] = useState<ItemAuditEntry[]>([]);
  const [actions, setActions] = useState<string[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [endpointMissing, setEndpointMissing] = useState(false);

  const [q, setQ] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [action, setAction] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setEndpointMissing(false);
    try {
      const r = await api.items.auditLog({
        q: q.trim() || undefined,
        from: dateOnlyStartToIso(from) || undefined,
        to: dateOnlyEndToIso(to) || undefined,
        action: action || undefined,
        limit: PAGE_LIMIT,
      });
      setEntries(r.entries || []);
      setActions(r.actions || []);
      setTotal(r.total ?? (r.entries || []).length);
    } catch (e: unknown) {
      // Worker chưa ship endpoint tổng hợp → UI vẫn đứng vững, chỉ báo đang chờ.
      if (e instanceof ApiError && (e.status === 404 || e.status === 405)) {
        setEndpointMissing(true);
      } else {
        setError((e as Error).message || "Không tải được lịch sử thay đổi");
      }
    } finally {
      setLoading(false);
    }
  }, [q, from, to, action]);

  useEffect(() => { load(); }, [load]);

  return (
    <GlassPanel title="Lịch sử thay đổi" badge={`${total} mục`}>
      <div className="filter-bar" style={{ flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
        <input
          type="text"
          placeholder="Tìm theo tên hàng / SKU"
          aria-label="Tìm hàng hoá"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          style={{ flex: 1, minWidth: 200 }}
        />
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--text-secondary)" }}>
          Từ
          <input type="date" aria-label="Từ ngày" value={from} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--text-secondary)" }}>
          Đến
          <input type="date" aria-label="Đến ngày" value={to} onChange={(e) => setTo(e.target.value)} />
        </label>
        <Select
          value={action}
          ariaLabel="Lọc theo loại thao tác"
          options={[
            { value: "", label: "Mọi thao tác" },
            ...actions.map((a) => ({ value: a, label: actionLabel(a) })),
          ]}
          onChange={setAction}
        />
      </div>

      {loading ? (
        <SkeletonTable rows={8} />
      ) : endpointMissing ? (
        <div role="status" style={{ padding: "10px 12px", borderRadius: 8, background: "var(--status-amber-bg)", border: "1px solid var(--status-amber-border)", color: "var(--status-amber)", fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}>
          <span className="material-symbols-outlined" style={{ fontSize: 16 }}>schedule</span>
          Máy chủ chưa có endpoint lịch sử tổng hợp — trang đã sẵn, sẽ hoạt động ngay khi worker cập nhật.
        </div>
      ) : error ? (
        <ErrorState error={error} onRetry={load} />
      ) : entries.length === 0 ? (
        <div className="empty">Không có thay đổi nào khớp bộ lọc</div>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Thời gian</th>
                <th>Hàng hoá</th>
                <th>Thao tác</th>
                <th>Trường</th>
                <th>Cũ → Mới</th>
                <th>Người / Nguồn</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((log) => (
                <tr key={log.id}>
                  <td style={{ whiteSpace: "nowrap", color: "var(--text-tertiary)", fontSize: 12 }}>
                    {formatTime(log.changed_at)}
                  </td>
                  <td>
                    <div style={{ fontWeight: 600 }}>{log.item_name || log.item_id}</div>
                    {log.sku && <div style={{ fontSize: 11, color: "var(--text-tertiary)", fontFamily: "var(--font-mono)" }}>{log.sku}</div>}
                  </td>
                  <td>{actionLabel(log.action)}</td>
                  <td style={{ color: "var(--text-secondary)", fontSize: 12 }}>{log.field_changed || "—"}</td>
                  <td style={{ fontSize: 12 }}>
                    {log.old_value != null || log.new_value != null
                      ? <><span style={{ color: "var(--text-tertiary)" }}>{log.old_value ?? "—"}</span> → <strong>{log.new_value ?? "—"}</strong></>
                      : "—"}
                  </td>
                  <td style={{ fontSize: 11, color: "var(--text-tertiary)" }}>
                    {log.changed_by || "—"}{log.source ? ` (${log.source})` : ""}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {total > entries.length && (
            <div style={{ padding: "8px 12px", fontSize: 12, color: "var(--text-tertiary)" }}>
              Hiển thị {entries.length} / {total} — thu hẹp bộ lọc để xem thêm.
            </div>
          )}
        </div>
      )}
    </GlassPanel>
  );
}
